import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { createConsumer, type Consumer, type Subscription } from '@rails/actioncable';
import { useAuth } from '../hooks/useAuth';
import { notifications } from '@mantine/notifications';
import { config } from '../config';

interface WebSocketContextType {
  consumer: Consumer | null;
  isConnected: boolean;
  subscriptions: Map<string, Subscription>;
  subscribe: (channel: string, params: any, callbacks: SubscriptionCallbacks) => Subscription | null;
  unsubscribe: (channel: string) => void;
  unsubscribeAll: () => void;
}

interface SubscriptionCallbacks {
  received?: (data: any) => void;
  connected?: () => void;
  disconnected?: () => void;
  rejected?: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { tokens, isAuthenticated } = useAuth();
  const [consumer, setConsumer] = useState<Consumer | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscriptions = useRef<Map<string, Subscription>>(new Map());

  // Initialize ActionCable consumer
  useEffect(() => {
    if (!isAuthenticated || !tokens?.access_token) {
      return;
    }

    const wsUrl = config.websocket.url;
    const consumerInstance = createConsumer(`${wsUrl}?token=${tokens.access_token}`);
    
    setConsumer(consumerInstance);
    setIsConnected(true);

    // Monitor connection
    const connectionMonitor = consumerInstance.connection.monitor;
    const originalReconnected = connectionMonitor.reconnected?.bind(connectionMonitor);
    const originalDisconnected = connectionMonitor.disconnected?.bind(connectionMonitor);

    connectionMonitor.reconnected = () => {
      setIsConnected(true);
      notifications.show({
        title: 'Connected',
        message: 'Real-time updates restored',
        color: 'green',
      });
      originalReconnected?.();
    };

    connectionMonitor.disconnected = () => {
      setIsConnected(false);
      originalDisconnected?.();
    };

    return () => {
      // Unsubscribe all channels
      subscriptions.current.forEach((subscription) => {
        subscription.unsubscribe();
      });
      subscriptions.current.clear();
      
      // Disconnect consumer
      consumerInstance.disconnect();
      setConsumer(null);
      setIsConnected(false);
    };
  }, [isAuthenticated, tokens?.access_token]);

  const subscribe = useCallback((
    channel: string,
    params: any,
    callbacks: SubscriptionCallbacks
  ): Subscription | null => {
    if (!consumer) {
      console.warn('WebSocket consumer not initialized');
      return null;
    }

    // Unsubscribe if already subscribed to this channel
    const existingSubscription = subscriptions.current.get(channel);
    if (existingSubscription) {
      existingSubscription.unsubscribe();
      subscriptions.current.delete(channel);
    }

    // Create new subscription
    const subscription = consumer.subscriptions.create(
      { channel, ...params },
      {
        connected() {
          console.log(`Connected to ${channel}`);
          callbacks.connected?.();
        },
        disconnected() {
          console.log(`Disconnected from ${channel}`);
          callbacks.disconnected?.();
        },
        rejected() {
          console.error(`Subscription rejected for ${channel}`);
          callbacks.rejected?.();
          notifications.show({
            title: 'Connection Error',
            message: 'Failed to connect to real-time updates',
            color: 'red',
          });
        },
        received(data: any) {
          callbacks.received?.(data);
        },
      }
    );

    subscriptions.current.set(channel, subscription);
    return subscription;
  }, [consumer]);

  const unsubscribe = useCallback((channel: string) => {
    const subscription = subscriptions.current.get(channel);
    if (subscription) {
      subscription.unsubscribe();
      subscriptions.current.delete(channel);
    }
  }, []);

  const unsubscribeAll = useCallback(() => {
    subscriptions.current.forEach((subscription) => {
      subscription.unsubscribe();
    });
    subscriptions.current.clear();
  }, []);

  const value: WebSocketContextType = {
    consumer,
    isConnected,
    subscriptions: subscriptions.current,
    subscribe,
    unsubscribe,
    unsubscribeAll,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;